import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs/operators';
import {
  DailySyncMode, DailySyncState, TransactionDailySyncService
} from 'src/app/data/service/transaction-daily-sync.service';

export interface DailySyncAccount {
  /** The Xero AccountID. */
  accountID: string;
  name: string;
  kind?: 'card' | 'bank';
  /** "Credit card · •••• 1234". */
  detail?: string;
}

export interface DailySyncModalOptions {
  organisationId: string;
  accounts: DailySyncAccount[];
  /** Back from the upgrade modal with a plan that includes it. */
  justUpgraded?: boolean;
}

/** 'upgrade' asks the opener to show the upgrade modal and bring this one back. */
export type DailySyncModalResult = 'activated' | 'saved' | 'deactivated' | 'upgrade' | null;

/** A timezone in the picker: its id, how it reads, and its offset now, all searchable. */
export interface ZoneOption {
  id: string;
  label: string;
  offset: string;
}

/** The common zones, when the browser cannot list them all. */
const FALLBACK_ZONES = [
  'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Adelaide', 'Australia/Perth',
  'Australia/Hobart', 'Australia/Darwin', 'Pacific/Auckland', 'Asia/Singapore', 'Europe/London',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'UTC'
];

/**
 * The daily transaction sync, from the Import panel: switch it on with the accounts to import and
 * the timezone whose 10:00 it runs at, change those while it is on, or switch it off. The same
 * shape as Inbox Automator's modal: a paid feature with an upgrade path.
 */
@Component({
  selector: 'app-daily-sync-modal',
  templateUrl: './daily-sync-modal.component.html',
  styleUrls: ['../../../../shared/billing/inbox-automator/inbox-automator-modal.component.scss', './daily-sync-modal.component.scss']
})
export class DailySyncModalComponent implements OnInit, OnDestroy {

  @Input() options: DailySyncModalOptions;

  state: DailySyncState | null = null;
  loading = true;
  busy = false;
  errorMessage = '';
  result: DailySyncModalResult = null;

  /** While switching on: what the server is doing, said in turn so a long wait is not silent. */
  progressLine = '';
  private progressTimers: any[] = [];

  chosen: { [accountId: string]: boolean } = {};
  accountQuery = '';
  timeZoneId = '';
  zoneOptions: ZoneOption[] = this.toZoneOptions(this.listZones());

  constructor(public activeModal: NgbActiveModal, private dailySync: TransactionDailySyncService) { }

  ngOnInit(): void {
    this.dailySync.state(this.options.organisationId)
      .pipe(finalize(() => this.loading = false))
      .subscribe(
        state => {
          this.state = state;
          const saved = new Set((state.accountIds || []).map(id => id.toLowerCase()));
          // Switched on: what was chosen. First time: every account, which is what most want.
          this.accounts.forEach(a => this.chosen[a.accountID] = saved.size > 0 ? saved.has(a.accountID.toLowerCase()) : true);
          this.timeZoneId = state.timeZoneId || state.suggestedTimeZoneId || this.browserZone();
          if (!this.zoneOptions.some(z => z.id === this.timeZoneId)) {
            this.zoneOptions = this.toZoneOptions([this.timeZoneId]).concat(this.zoneOptions);
          }
        },
        () => this.errorMessage = 'The daily sync could not be loaded. Close this and try again.');
  }

  get accounts(): DailySyncAccount[] {
    return (this.options && this.options.accounts) || [];
  }

  get mode(): DailySyncMode {
    return TransactionDailySyncService.mode(this.state);
  }

  /** Accounts and timezone are chosen when it can be switched on, and changed while it is on. */
  get canEdit(): boolean {
    return this.mode === 'activate' || this.mode === 'active';
  }

  /** The accounts matching the search, by name or detail ("card", "7913"). */
  get filteredAccounts(): DailySyncAccount[] {
    const q = this.accountQuery.trim().toLowerCase();
    if (!q) { return this.accounts; }
    return this.accounts.filter(a => `${a.name || ''} ${a.detail || ''}`.toLowerCase().includes(q));
  }

  get chosenIds(): string[] {
    return this.accounts.filter(a => this.chosen[a.accountID]).map(a => a.accountID);
  }

  /** Select all / Clear all act on what the search shows. */
  get allChosen(): boolean {
    const shown = this.filteredAccounts;
    return shown.length > 0 && shown.every(a => this.chosen[a.accountID]);
  }

  toggleAll(on: boolean): void {
    this.filteredAccounts.forEach(a => this.chosen[a.accountID] = on);
  }

  /** "Sydney", "australia", "new york" and "gmt+10" all find their zone. */
  readonly searchZone = (term: string, zone: ZoneOption): boolean => {
    const q = (term || '').trim().toLowerCase();
    return `${zone.label} ${zone.id} ${zone.offset}`.toLowerCase().includes(q);
  }

  /** "12 new transactions on 16 Oct", "Failed on 16 Oct: …". */
  get lastRunLine(): string {
    const s = this.state;
    if (!s || !s.lastRunUtc) { return ''; }
    const when = new Date(s.lastRunUtc).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    if (s.lastRunStatus === 'success') {
      const n = s.lastRunImported || 0;
      return `Last run ${when}: ${n === 0 ? 'no new transactions' : n + ' new transaction' + (n === 1 ? '' : 's')}.`;
    }
    return `Last run ${when}: ${s.lastRunMessage || 'it did not finish'}.`;
  }

  save(): void {
    if (this.busy) { return; }
    if (this.chosenIds.length === 0) { this.errorMessage = 'Choose at least one bank account.'; return; }
    if (!this.timeZoneId) { this.errorMessage = 'Choose a timezone.'; return; }
    const wasActive = this.mode === 'active';
    this.busy = true;
    this.errorMessage = '';
    this.startProgress(wasActive);
    this.dailySync.activate(this.options.organisationId, this.chosenIds, this.timeZoneId)
      .pipe(finalize(() => { this.busy = false; this.stopProgress(); }))
      .subscribe(
        res => {
          if (res && res.workflowError) {
            this.errorMessage = `Switched on, but its workflow could not be set up: ${res.workflowError}`;
            return;
          }
          this.activeModal.close(wasActive ? 'saved' : 'activated');
        },
        err => this.errorMessage = err?.status === 403
          ? 'Your plan doesn’t include the daily sync, or only an admin can switch it on.'
          : (err?.error?.errorMessage || 'The daily sync could not be switched on. Please try again.'));
  }

  deactivate(): void {
    if (this.busy) { return; }
    this.busy = true;
    this.errorMessage = '';
    this.dailySync.deactivate(this.options.organisationId)
      .pipe(finalize(() => this.busy = false))
      .subscribe(
        () => this.activeModal.close('deactivated'),
        err => this.errorMessage = err?.error?.errorMessage || 'The daily sync could not be switched off. Please try again.');
  }

  ngOnDestroy(): void {
    this.stopProgress();
  }

  /**
   * Switching on sets up the sync's workflow, which can take a while the first time. The steps
   * are named as they go (on timers: the server reports nothing until it is done), so a long
   * wait reads as work rather than as a hang.
   */
  private startProgress(saving: boolean): void {
    this.stopProgress();
    this.progressLine = saving ? 'Saving your changes…' : 'Saving your choices…';
    if (saving) { return; }
    this.progressTimers.push(
      setTimeout(() => this.progressLine = 'Setting up the daily workflow…', 2500),
      setTimeout(() => this.progressLine = `Scheduling it for ${this.state?.runTime || '10:00'} in ${this.timeZoneId.split('_').join(' ')}…`, 9000),
      setTimeout(() => this.progressLine = 'Still working. The first switch-on can take up to a minute; you can leave this open.', 18000));
  }

  private stopProgress(): void {
    this.progressTimers.forEach(t => clearTimeout(t));
    this.progressTimers = [];
    this.progressLine = '';
  }

  upgrade(): void {
    this.activeModal.close('upgrade');
  }

  close(): void {
    this.activeModal.close(this.result);
  }

  private browserZone(): string {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
  }

  private toZoneOptions(ids: string[]): ZoneOption[] {
    return ids.map(id => ({ id, label: id.replace(/_/g, ' '), offset: this.zoneOffset(id) }));
  }

  /** "GMT+10", as the zone stands today; empty where the browser cannot say. */
  private zoneOffset(id: string): string {
    try {
      const part = new Intl.DateTimeFormat('en-US', { timeZone: id, timeZoneName: 'shortOffset' } as any)
        .formatToParts(new Date()).find(p => p.type === 'timeZoneName');
      return part ? part.value : '';
    } catch { return ''; }
  }

  private listZones(): string[] {
    try {
      const all = (Intl as any).supportedValuesOf ? (Intl as any).supportedValuesOf('timeZone') as string[] : null;
      if (all && all.length) { return all.slice(); }
    } catch { }
    return FALLBACK_ZONES.slice();
  }
}
